import React, { useState, useRef, useEffect } from "react"
import { ArrowRight, X } from "lucide-react"
import { Turnstile } from "@marsidev/react-turnstile"
import { useLang } from "./i18n/LangContext"
import { useWaitlistModal } from "./WaitlistModalContext"
import { track } from "../../analytics/mixpanel"

const TURNSTILE_SITE_KEY = document.querySelector('meta[name="turnstile-site-key"]')?.content
const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

export function WaitlistModal() {
  const { t } = useLang()
  const { open, closeModal } = useWaitlistModal()

  const [email, setEmail] = useState("")
  const [businessName, setBusinessName] = useState("")
  const [appUrl, setAppUrl] = useState("")
  const [status, setStatus] = useState(null) // null | "ok" | "already_registered" | "error"
  const [errors, setErrors] = useState([])
  const [loading, setLoading] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [turnstileError, setTurnstileError] = useState(false)
  const turnstileRef = useRef()
  const emailRef = useRef(null)

  // Close on Escape and lock body scroll while open.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === "Escape") closeModal() }
    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    emailRef.current?.focus()
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [open, closeModal])

  // Reset form state each time the modal is opened.
  useEffect(() => {
    if (open) {
      setStatus(null)
      setErrors([])
      setTurnstileError(false)
    }
  }, [open])

  if (!open) return null

  const submitted = status === "ok" || status === "already_registered"

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email || loading) return
    if (TURNSTILE_SITE_KEY && !turnstileToken) return
    setLoading(true)
    setErrors([])
    try {
      const res = await fetch("/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({
          email,
          business_name: businessName,
          shopify_app_url: appUrl,
          turnstile_token: turnstileToken,
        }),
      })
      const data = await res.json()
      setStatus(data.status)
      track("Waitlist Signup Submitted", {
        status: data.status,
        email_domain: email.includes("@") ? email.split("@").pop() : null,
        has_business_name: businessName.trim().length > 0,
        has_shopify_app_url: appUrl.trim().length > 0,
      })
      if (data.status === "error") setErrors(data.errors || [])
      if (data.status !== "ok") turnstileRef.current?.reset()
    } catch {
      setStatus("error")
      track("Waitlist Signup Submitted", { status: "error" })
      turnstileRef.current?.reset()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="waitlist-modal-title"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />

      <div className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-900 shadow-xl ring-1 ring-gray-200 dark:ring-gray-800 p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={closeModal}
          aria-label={t.modal.close}
          className="absolute right-4 top-4 rounded-md p-1.5 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {submitted ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <span className="text-lg font-semibold text-gray-900 dark:text-white">
              {status === "already_registered" ? t.modal.alreadyRegistered : t.modal.successTitle}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">{t.modal.successBody}</span>
            <button
              type="button"
              onClick={closeModal}
              className="mt-4 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-600 transition-colors"
            >
              {t.modal.done}
            </button>
          </div>
        ) : (
          <>
            <h2 id="waitlist-modal-title" className="text-xl font-bold text-gray-900 dark:text-white pr-8">
              {t.modal.title}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t.modal.subtitle}</p>

            <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
              <div>
                <label htmlFor="wl-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.modal.emailLabel} <span className="text-red-500">*</span>
                </label>
                <input
                  id="wl-email"
                  ref={emailRef}
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t.modal.emailPlaceholder}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-400"
                />
              </div>

              <div>
                <label htmlFor="wl-business" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.modal.businessLabel}
                </label>
                <input
                  id="wl-business"
                  type="text"
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                  placeholder={t.modal.businessPlaceholder}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-400"
                />
              </div>

              <div>
                <label htmlFor="wl-appurl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.modal.appUrlLabel}
                </label>
                <input
                  id="wl-appurl"
                  type="url"
                  value={appUrl}
                  onChange={e => setAppUrl(e.target.value)}
                  placeholder={t.modal.appUrlPlaceholder}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-600 dark:focus:ring-brand-400"
                />
              </div>

              {errors.length > 0 && (
                <ul className="text-xs text-red-500 list-disc list-inside">
                  {errors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              )}

              {TURNSTILE_SITE_KEY && (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => { setTurnstileToken(token); setTurnstileError(false) }}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => { setTurnstileToken(null); setTurnstileError(true) }}
                  options={{ theme: "auto", size: "flexible" }}
                />
              )}
              {turnstileError && (
                <p className="text-xs text-red-500">{t.modal.verificationError}</p>
              )}

              <button
                type="submit"
                disabled={loading || (TURNSTILE_SITE_KEY && !turnstileToken)}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-5 py-3 text-sm font-semibold text-white hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-600 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? t.modal.sending : <>{t.modal.submit} <ArrowRight className="h-4 w-4" /></>}
              </button>

              <p className="text-center text-xs text-gray-400 dark:text-gray-500">{t.modal.hint}</p>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
